import { isServer, keepPreviousData, QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { routeTree } from './routeTree.gen';
import {
	persistQueryClient,
	PersistQueryClientProvider,
	removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { ReactNode } from 'react';
import { gtag } from '@worldcoo/ui/lib/analytics';
import { loadLandingTranslation } from './lib/i18n';
import { getRequestContext, logErrorFn } from './lib/server-functions-portal';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { DefaultCatchBoundary } from './routes/-components/default-catch-boundary';
import { extractErrorInfo } from './lib/error-utils';
import { NotFound } from './routes/-components/not-found';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { VERSION_SKEW_RELOAD_KEY, VERSION_SKEW_RELOAD_COOLDOWN_MS } from './routes/-components/default-catch-boundary';

/**
 * During a version skew reload, the DOM is being torn down while React streaming SSR
 * scripts still execute, causing benign errors (e.g., "Cannot read properties of null
 * (reading 'parentNode')"). Suppress logging these to the backend.
 */
function isVersionSkewReloadInProgress() {
	const lastReload = window.sessionStorage.getItem(VERSION_SKEW_RELOAD_KEY);
	return !!lastReload && Date.now() - parseInt(lastReload) < VERSION_SKEW_RELOAD_COOLDOWN_MS;
}

/**
 * TODO: We're using the tanstack start built-in RPC instead of trpc because trpc
 * needs to send the proper headers to the backend and this disables tanstack query persist
 * We lose the batching from TRPC but we gain the ability to persist queries
 */
export async function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60 * 1000,
				gcTime: Infinity,
				placeholderData: keepPreviousData,
			},
		},
	});

	const persister = createAsyncStoragePersister({
		storage: typeof window !== 'undefined' ? window.localStorage : undefined,
		retry: removeOldestQuery,
	});

	const buster = 'v3';

	// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
	// because on quick reload is still not restoring from persister
	if (!isServer) {
		persistQueryClient({ queryClient, persister, buster });
	}

	// Server: compute request context from headers once. Client: placeholder until hydrate().
	const serverContext = isServer ? await getRequestContext() : undefined;
	const requestContext = {
		baseUrl: serverContext?.baseUrl ?? '',
		portalId: serverContext?.portalId ?? '',
		locale: serverContext?.locale ?? 'es',
		isBot: serverContext?.isBot ?? false,
		isInIframe: serverContext?.isInIframe ?? false,
	};

	await loadLandingTranslation(
		isServer ? requestContext.locale : (document.documentElement.lang as PortalLocale) || 'es',
	);

	const router = createTanStackRouter({
		routeTree,
		context: { queryClient, ...requestContext },
		// Dehydrate server-computed request context so the client receives it
		// without a server function call on every navigation.
		dehydrate: () => ({ requestContext }),
		hydrate: (dehydrated) => {
			if (dehydrated.requestContext && router.options.context) {
				Object.assign(router.options.context, dehydrated.requestContext);
			}
		},
		defaultPreload: 'intent',
		scrollRestoration: true,
		rewrite: {
			input: ({ url }) => {
				// Skip rewriting for /media/* (imgproxy proxy route)
				if (url.pathname.startsWith('/media/')) return url;

				// Check if it's the main domain (no subdomain or www)
				const parts = url.hostname.split('.');
				if (
					parts[0] === 'www' ||
					(parts.length < 3 && parts.at(-1) !== 'localhost') ||
					(parts.length < 2 && parts.at(-1) === 'localhost')
				) {
					// Rewrite to /www/...
					url.pathname = `/www${url.pathname}`;
				} else {
					// Extract portalId from subdomain (e.g., eroski.example.com -> eroski)
					const parts = url.hostname.split('.');
					const portalId = parts[0];
					url.pathname = `/${portalId}${url.pathname}`;
				}
				return url;
			},
			output: ({ url }) => {
				// Skip rewriting for /media/* (imgproxy proxy route)
				if (url.pathname.startsWith('/media/')) return url;

				// Remove the portalId prefix from the pathname for clean URLs
				const parts = url.pathname.split('/').filter(Boolean);
				if (parts.length > 0) {
					if (parts[0] === 'www') {
						// Remove /www prefix
						url.pathname = '/' + parts.slice(1).join('/') || '/';
					} else {
						// Remove portalId prefix (it's already in the subdomain)
						url.pathname = '/' + parts.slice(1).join('/') || '/';
					}
				}
				return url;
			},
		},
		// Since we're using React Query, we don't want loader calls to ever be stale
		// This will ensure that the loader is always called when the route is preloaded or visited
		defaultPreloadStaleTime: 0,
		Wrap: (props: { children: ReactNode }) => {
			return (
				<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster }}>
					{props.children}
				</PersistQueryClientProvider>
			);
		},
		defaultErrorComponent: (props) => (
			<I18nProvider i18n={i18n}>
				<DefaultCatchBoundary {...props} />
			</I18nProvider>
		),
		defaultNotFoundComponent: () => <NotFound />,
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
		wrapQueryClient: false,
	});

	if (!isServer) {
		/**
		 * Setup google analytics
		 */
		gtag('js', new Date());
		gtag('config', import.meta.env.VITE_ANALYTICS_CODE);

		router.history.subscribe((url) => {
			gtag('event', 'page_view', { page_location: url.location.href });
		});

		/**
		 * Reload the page if it fails to load dynamic imports
		 * When a new deployment occurs, the hosting service may delete the assets from previous deployments.
		 * As a result, a user who visited your site before the new deployment might encounter an import error.
		 * This error happens because the assets running on that user's device are outdated and it tries to import
		 * the corresponding old chunk, which is deleted.
		 * This event is useful for addressing this situation. In this case, make sure to set Cache-Control: no-cache on the HTML file,
		 * otherwise the old assets will be still referenced.
		 * See: https://vite.dev/guide/build#load-error-handling
		 */
		window.addEventListener('vite:preloadError', () => {
			window.location.reload();
		});

		/**
		 * Global error handlers for errors that escape React error boundaries
		 * (async callbacks, event handlers, setTimeout, etc.)
		 */
		window.addEventListener('error', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			// Cross-origin script errors (reCAPTCHA, GA, etc.) report event.error as null.
			// These are not actionable — skip logging them.
			if (event.error == null) return;
			const errorInfo = extractErrorInfo(event.error);
			logErrorFn({
				data: {
					route: window.location.pathname,
					name: '[GLOBAL] ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch(() => {});
		});

		window.addEventListener('unhandledrejection', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			const errorInfo = extractErrorInfo(event.reason);
			logErrorFn({
				data: {
					route: window.location.pathname,
					name: '[UNHANDLED REJECTION] ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch(() => {});
		});
	}

	return router;
}
